import React, { useState } from 'react';
import PrintButton from '@/components/PrintButton';
import { 
    Plus, 
    Download, 
    Trash2, 
    Eye,
    Send,
    PackageCheck,
    Pencil,
    FileText,
    AlertTriangle,
    Save,
    Scale,
    Calendar,
    Copy,
    MessageCircle,
} from 'lucide-react';
import { formatCurrency, formatDate, getStatusLabel, getStatusClass } from '@/lib/utils';
import { ordersAPI, openAuthPdf } from '@/lib/api';
import HideAddedToggle from './components/HideAddedToggle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    ManualReceptionDetailDialog,
    DeleteManualReceptionAlert,
} from './dialogs/ManualReceptionDialogs';
import EditReceptionDialog from './dialogs/EditReceptionDialog';
import CreateOrderDialog from './dialogs/CreateOrderDialog';
import OrderableModeToggle from './components/OrderableModeToggle';
import WhatsAppShareDialog from './dialogs/WhatsAppShareDialog';
import CategorizedProductSelectContent from './components/CategorizedProductSelectContent';

// Patterns auto-generated by the backend during reception flows.
// These technical annotations must NOT be shown to the supplier on the PDF
// nor displayed inside the table footer as the supplier-facing note.
const SYSTEM_NOTE_PREFIXES = [
    'Quantites restantes cloturees le',
    'Tarifs ajustes a la reception',
    'Produits supplementaires ajoutes a la reception',
];

function splitOrderNotes(rawNotes) {
    const text = (rawNotes || '').trim();
    if (!text) return { userNote: '', systemNotes: [] };
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    const userLines = [];
    const systemLines = [];
    for (const line of lines) {
        if (SYSTEM_NOTE_PREFIXES.some(p => line.startsWith(p))) {
            systemLines.push(line);
        } else {
            userLines.push(line);
        }
    }
    return { userNote: userLines.join('\n'), systemNotes: systemLines };
}

export default function OrdersDialogs(props) {
    const {
        // Data
        suppliers, orderCreationSuppliers, sites, supplierProducts, industrialProducts,
        availableSupplierProducts, availableManualReceptionProducts,
        hideAddedProducts, setHideAddedProducts,
        orderableMode, setOrderableMode,
        manualReceptionOrderableMode, setManualReceptionOrderableMode,
        receptionOrderableMode, setReceptionOrderableMode,
        // Dialog states
        createDialog, setCreateDialog,
        detailDialog, setDetailDialog,
        deleteDialog, setDeleteDialog,
        receptionDialog, setReceptionDialog,
        editReceptionDialog, setEditReceptionDialog,
        manualReceptionDialog, setManualReceptionDialog,
        manualReceptionDetailDialog, setManualReceptionDetailDialog,
        deleteManualReceptionDialog, setDeleteManualReceptionDialog,
        // Selected entities
        selectedOrder, selectedManualReception,
        orderToDelete, manualReceptionToDelete,
        // Forms
        orderForm, setOrderForm,
        currentItem, setCurrentItem,
        addItemToOrder, setAddItemToOrder,
        addReceptionItem, setAddReceptionItem,
        receptionHistory,
        editingReception, setEditingReception,
        receptionItems, setReceptionItems,
        extraReceptionItem, setExtraReceptionItem,
        availableExtraReceptionProducts, handleAddExtraReceptionItem,
        replacementDialog, setReplacementDialog,
        replacementForm, setReplacementForm,
        handleOpenReplacement, handleConfirmReplacement,
        closeRemainingQuantities, setCloseRemainingQuantities,
        documentNumber, setDocumentNumber,
        receptionDate, setReceptionDate,
        manualReceptionForm, setManualReceptionForm,
        manualReceptionItem, setManualReceptionItem,
        editingManualReception, setEditingManualReception,
        // Edit state
        editOrderMode, setEditOrderMode, editOrderItems, setEditOrderItems, editOrderNotes, setEditOrderNotes,
        editOrderDate, setEditOrderDate, editOrderDocNumber, setEditOrderDocNumber,
        // Handlers
        getAllowedSites,
        handleAddItem, handleRemoveItem, handleCreateOrder,
        handleStartEditOrder, handleCancelEditOrder, handleAddItemToEditOrder,
        isOrderInReceptionMode, handleAddReceptionItemDirect,
        handleRemoveItemFromEditOrder, handleUpdateOrderItemQuantity, handleUpdateOrderItemReceived, handleUpdateOrderItemMode, handleSaveOrderChanges,
        handleConfirmDelete,
        handleSaveReception, hasRemainingQuantities, hasUnreceivedQuantities,
        handleSaveReceptionDraft,
        receptionSaving, manualReceptionSaving,
        handleEditReception, handleSaveReceptionEdit, handleDeleteReception,
        handleAddManualReceptionItem, handleRemoveManualReceptionItem,
        calculateManualReceptionTotal, handleSaveManualReception,
        handleConfirmDeleteManualReception,
        handleEditManualReception, handleDuplicateManualReception, handleDeleteManualReceptionClick,
        // Helpers
        calculateLineTotal, calculateTotal, getBillingInfo, getOrderModeLabel, getSupplierProductCategory,
        // Permissions
        canEdit, canDelete, canSendEmail, canSendWhatsApp, canCreateReception,
        handleSendEmail,
    } = props;

    // Local state: resend confirmation dialog (for orders already sent).
    const [resendConfirm, setResendConfirm] = useState(false);
    // Local state: WhatsApp share dialog (opened from order detail view).
    const [whatsappDialog, setWhatsappDialog] = useState(false);

    return (
        <>
{/* Create Order Dialog */}
<CreateOrderDialog
    open={createDialog}
    onOpenChange={setCreateDialog}
    orderForm={orderForm}
    setOrderForm={setOrderForm}
    currentItem={currentItem}
    setCurrentItem={setCurrentItem}
    suppliers={orderCreationSuppliers || suppliers}
    sites={sites}
    supplierProducts={supplierProducts}
    availableSupplierProducts={availableSupplierProducts}
    getAllowedSites={getAllowedSites}
    getSupplierProductCategory={getSupplierProductCategory}
    getBillingInfo={getBillingInfo}
    getOrderModeLabel={getOrderModeLabel}
    calculateLineTotal={calculateLineTotal}
    calculateTotal={calculateTotal}
    onAddItem={handleAddItem}
    onRemoveItem={handleRemoveItem}
    onCreateOrder={handleCreateOrder}
    hideAddedProducts={hideAddedProducts}
    setHideAddedProducts={setHideAddedProducts}
    orderableMode={orderableMode}
    setOrderableMode={setOrderableMode}
/>

{/* Order Detail Dialog */}
<Dialog open={detailDialog} onOpenChange={(open) => {
    if (!open && detailDialog) {
        window.history.back();
    }
    setDetailDialog(open);
    if (!open) {
        setEditOrderMode(false);
        setEditOrderItems([]);
        setOrderableMode('normal');  // never persist exceptional mode across orders
    }
}}>
    <DialogContent className="sm:max-w-[95vw] sm:w-full sm:h-[95vh] overflow-hidden flex flex-col">
        {/* Compact header: title + info on same row */}
        <div className="flex-shrink-0 flex items-center gap-4 pb-2 border-b flex-wrap pr-12 sm:pr-14">
            <h2 className="text-lg font-semibold whitespace-nowrap">
                {editOrderMode ? 'Modifier' : 'Détail'} {selectedOrder?.order_number}
            </h2>
            {editOrderMode && (
                <OrderableModeToggle mode={orderableMode} onChange={setOrderableMode} />
            )}
            {selectedOrder && (
                <div className="flex items-center gap-4 text-xs flex-1 min-w-0 flex-wrap">
                    <span className="text-slate-500">{selectedOrder.supplier_name}</span>
                    <span className="text-slate-400">|</span>
                    <span className="text-slate-500">{selectedOrder.site_name}</span>
                    <span className="text-slate-400">|</span>
                    {editOrderMode ? (
                        <div className="flex items-center gap-2">
                            <Label className="text-xs text-slate-500 whitespace-nowrap">Date :</Label>
                            <Input
                                type="date"
                                value={editOrderDate}
                                onChange={(e) => setEditOrderDate(e.target.value)}
                                className="!bg-emerald-50/40 !border-2 !border-emerald-200 hover:!border-emerald-400 focus-visible:!border-emerald-500 h-7 text-xs w-[150px]"
                                data-testid="edit-order-date-input"
                            />
                        </div>
                    ) : (
                        <span className="text-slate-500">{formatDate(selectedOrder.received_at || selectedOrder.created_at)}</span>
                    )}
                    <span className="text-slate-400">|</span>
                    {editOrderMode ? (
                        <div className="flex items-center gap-2">
                            <Label className="text-xs text-slate-500 whitespace-nowrap">N° doc :</Label>
                            <Input
                                type="text"
                                placeholder="Ex: FAC-2024-001"
                                value={editOrderDocNumber}
                                onChange={(e) => setEditOrderDocNumber(e.target.value)}
                                className="!bg-emerald-50/40 !border-2 !border-emerald-200 hover:!border-emerald-400 focus-visible:!border-emerald-500 h-7 text-xs w-[180px]"
                                data-testid="edit-order-doc-input"
                            />
                        </div>
                    ) : (() => {
                        const docs = [...new Set([
                            ...receptionHistory.map(r => r.document_number),
                            selectedOrder.document_number,
                            selectedOrder.invoice_number,
                        ].filter(Boolean))];
                        return docs.length > 0 && (
                            <span className="text-slate-500" data-testid="order-doc-numbers">{docs.join(', ')}</span>
                        );
                    })()}
                    <Badge className={`${getStatusClass(selectedOrder.status)} text-xs`}>
                        {getStatusLabel(selectedOrder.status)}
                    </Badge>
                </div>
            )}
            <div className="flex items-center gap-2 flex-shrink-0">
                {!editOrderMode && selectedOrder && (
                    <Button variant="outline" size="sm" onClick={handleStartEditOrder} className="flex items-center gap-2" data-testid="edit-order-btn">
                        <Pencil className="w-4 h-4" /> Modifier
                    </Button>
                )}
            </div>
        </div>
        {selectedOrder && (
            <>

                {/* Edit Mode - Add product section (compact) */}
                {editOrderMode && (
                    <div className="flex-shrink-0 border-b py-2">
                        {isOrderInReceptionMode(selectedOrder) ? (
                            /* Mode Réception - Ajouter un produit déjà réceptionné */
                            <div className="bg-emerald-50 p-3 rounded-lg">
                                <Label className="text-sm font-semibold mb-2 block text-emerald-700">
                                    <PackageCheck className="w-4 h-4 inline mr-2" />
                                    Ajouter un produit réceptionné
                                </Label>
                                <div className="grid grid-cols-12 gap-2 items-end">
                                    <div className="col-span-6 space-y-1">
                                        <Label className="text-xs text-slate-500">Produit</Label>
                                        <Select
                                            value={addReceptionItem.supplier_product_id}
                                            onValueChange={(value) => {
                                                const sp = supplierProducts.find(p => p.id === value);
                                                setAddReceptionItem({ 
                                                    ...addReceptionItem, 
                                                    supplier_product_id: value,
                                                    unit_price: sp?.unit_price || 0
                                                });
                                            }}
                                        >
                                            <SelectTrigger data-testid="add-reception-product-select">
                                                <SelectValue placeholder="Sélectionner un produit" />
                                            </SelectTrigger>
                                            <CategorizedProductSelectContent
                                                products={supplierProducts.filter(sp => sp.supplier_id === selectedOrder.supplier_id)}
                                                industrialProducts={industrialProducts}
                                            />
                                        </Select>
                                    </div>
                                    <div className="col-span-2 space-y-1">
                                        {(() => {
                                            const selectedSp = supplierProducts.find(p => p.id === addReceptionItem.supplier_product_id);
                                            const isVariableWeight = !!selectedSp?.variable_weight;
                                            const qtyUnit = selectedSp ? (selectedSp.invoice_unit || selectedSp.billing_unit || 'pièce') : null;
                                            return (
                                                <>
                                                    <Label className="text-xs text-slate-500">Qté{qtyUnit ? ` (${qtyUnit})` : ''}</Label>
                                                    <Input
                                                        type="number"
                                                        inputMode={isVariableWeight ? 'decimal' : 'numeric'}
                                                        min={isVariableWeight ? '0.01' : '1'}
                                                        step={isVariableWeight ? '0.01' : '1'}
                                                        value={addReceptionItem.quantity}
                                                        onFocus={(e) => e.target.select()}
                                                        onChange={(e) => {
                                                            const raw = e.target.value;
                                                            const parsed = isVariableWeight
                                                                ? (parseFloat(raw) || 0)
                                                                : (parseInt(raw) || 1);
                                                            setAddReceptionItem({ ...addReceptionItem, quantity: parsed });
                                                        }}
                                                        data-testid="add-reception-qty-input"
                                                    />
                                                </>
                                            );
                                        })()}
                                    </div>
                                    <div className="col-span-3 space-y-1">
                                        <Label className="text-xs text-slate-500">Prix unit.</Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={addReceptionItem.unit_price}
                                            onChange={(e) => setAddReceptionItem({ ...addReceptionItem, unit_price: parseFloat(e.target.value) || 0 })}
                                            data-testid="add-reception-price-input"
                                        />
                                    </div>
                                    <div className="col-span-1">
                                        <Button
                                            type="button"
                                            onClick={handleAddReceptionItemDirect}
                                            className="w-full bg-emerald-500 hover:bg-emerald-600"
                                            data-testid="add-reception-btn"
                                        >
                                            <Plus className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* Mode Commande - Ajouter un produit à commander */
                            <div className="bg-blue-50 p-2 rounded-lg">
                                <div className="flex items-center gap-3">
                                    <Label className="text-sm font-semibold text-blue-700 whitespace-nowrap shrink-0">Ajouter un produit :</Label>
                                    <Select
                                        value={addItemToOrder.supplier_product_id}
                                        onValueChange={(value) => setAddItemToOrder({ ...addItemToOrder, supplier_product_id: value, order_mode: 'packaging' })}
                                    >
                                        <SelectTrigger data-testid="edit-order-product-select" className="h-9 flex-1 min-w-0">
                                            <SelectValue placeholder="Sélectionner un produit" />
                                        </SelectTrigger>
                                        <CategorizedProductSelectContent
                                            products={supplierProducts
                                                .filter(sp => sp.supplier_id === selectedOrder.supplier_id && (orderableMode === 'all' || sp.commandable !== false))
                                                .filter(sp => !hideAddedProducts || !(editOrderItems || []).some(it => it.supplier_product_id === sp.id))}
                                            industrialProducts={industrialProducts}
                                            renderLabel={(sp) => `${sp.supplier_name_product || sp.industrial_product_name} (${formatCurrency(sp.unit_price)})`}
                                        />
                                    </Select>
                                    <HideAddedToggle
                                        checked={hideAddedProducts}
                                        onCheckedChange={setHideAddedProducts}
                                        testid="hide-added-toggle-edit-order"
                                    />
                                </div>
                                
                                {/* Mode and quantity selection - only show when product selected */}
                                {addItemToOrder.supplier_product_id && (() => {
                                    const sp = supplierProducts.find(p => p.id === addItemToOrder.supplier_product_id);
                                    if (!sp) return null;
                                    
                                    const orderUnit = sp.order_unit || 'pièce';
                                    const billingUnit = sp.billing_unit || 'pièce';
                                    const invoiceUnit = sp.invoice_unit || billingUnit;
                                    const packagingQty = sp.packaging_qty || 1;
                                    // Afficher le choix d'unité si les unités sont différentes
                                    const hasUnitChoice = orderUnit.toLowerCase() !== billingUnit.toLowerCase();
                                    
                                    const unitForQty = addItemToOrder.order_mode === 'detail' ? billingUnit : orderUnit;
                                    const qtyBilled = addItemToOrder.order_mode === 'detail' 
                                        ? addItemToOrder.quantity 
                                        : addItemToOrder.quantity * packagingQty;

                                    // Total € : respecte invoice_unit vs order_unit
                                    const invoicePerOrderUnit = sp.packaging_qty > 0
                                        && invoiceUnit.toLowerCase() === orderUnit.toLowerCase();
                                    const lineTotal = addItemToOrder.order_mode === 'detail' || !sp.packaging_qty || invoicePerOrderUnit
                                        ? addItemToOrder.quantity * sp.unit_price
                                        : addItemToOrder.quantity * packagingQty * sp.unit_price;
                                    
                                    return (
                                        <div className="space-y-2">
                                            {/* Affichage du produit sélectionné - compact */}
                                            <div className="p-2 bg-blue-100 rounded border border-blue-200 flex items-center justify-between">
                                                <div>
                                                    <span className="font-semibold text-blue-800 text-sm">
                                                        {sp.supplier_name_product || sp.industrial_product_name}
                                                    </span>
                                                    <span className="text-xs text-blue-600 ml-2">
                                                        ({formatCurrency(sp.unit_price)}/{invoiceUnit})
                                                    </span>
                                                </div>
                                            </div>
                                            
                                            {/* Mode selection et quantité sur une ligne */}
                                            <div className="flex items-end gap-3">
                                                {/* Mode selection */}
                                                {hasUnitChoice ? (
                                                    <div className="flex items-center gap-2">
                                                        <Label className="text-xs font-medium whitespace-nowrap">Mode :</Label>
                                                        <div className="flex gap-1">
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                variant={addItemToOrder.order_mode === 'packaging' ? 'default' : 'outline'}
                                                                onClick={() => setAddItemToOrder({ ...addItemToOrder, order_mode: 'packaging' })}
                                                                className={`h-8 text-xs ${addItemToOrder.order_mode === 'packaging' ? 'bg-blue-600' : ''}`}
                                                            >
                                                                {orderUnit}
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                variant={addItemToOrder.order_mode === 'detail' ? 'default' : 'outline'}
                                                                onClick={() => setAddItemToOrder({ ...addItemToOrder, order_mode: 'detail' })}
                                                                className={`h-8 text-xs ${addItemToOrder.order_mode === 'detail' ? 'bg-amber-600' : ''}`}
                                                            >
                                                                {billingUnit}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-xs text-slate-600">
                                                        Unité : <span className="font-semibold">{orderUnit}</span>
                                                    </div>
                                                )}
                                                
                                                {/* Quantité */}
                                                <div className="flex items-center gap-2">
                                                    <Label className="text-xs whitespace-nowrap">Qté ({unitForQty}) :</Label>
                                                    {(() => {
                                                        const isVariableWeight = !!sp?.variable_weight && addItemToOrder.order_mode !== 'packaging';
                                                        return (
                                                            <Input
                                                                type="number"
                                                                inputMode={isVariableWeight ? 'decimal' : 'numeric'}
                                                                min={isVariableWeight ? '0.01' : '1'}
                                                                step={isVariableWeight ? '0.01' : '1'}
                                                                value={addItemToOrder.quantity}
                                                                onFocus={(e) => e.target.select()}
                                                                onChange={(e) => {
                                                                    const raw = e.target.value;
                                                                    const parsed = isVariableWeight
                                                                        ? (parseFloat(raw) || 0)
                                                                        : (parseInt(raw) || 1);
                                                                    setAddItemToOrder({ ...addItemToOrder, quantity: parsed });
                                                                }}
                                                                className="w-20 h-8"
                                                                data-testid="edit-order-quantity-input"
                                                            />
                                                        );
                                                    })()}
                                                </div>
                                                
                                                {/* Facturation prévue */}
                                                <div className="text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded">
                                                    → {qtyBilled} {billingUnit} = {formatCurrency(lineTotal)}
                                                </div>
                                                
                                                {/* Bouton ajouter */}
                                                <Button
                                                    type="button"
                                                    onClick={handleAddItemToEditOrder}
                                                    className="bg-blue-500 hover:bg-blue-600 h-8"
                                                    data-testid="edit-order-add-btn"
                                                >
                                                    <Plus className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })()}
                                
                                {/* Show disabled input when no product selected */}
                                {!addItemToOrder.supplier_product_id && (
                                    <div className="flex items-center gap-2 text-sm text-slate-400 italic">
                                        Sélectionnez un produit pour l'ajouter à la commande
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                
                {/* Scrollable Content */}
                <div className="flex-1 overflow-auto py-4 space-y-4 min-h-0">
                    {/* Products table - Edit Mode */}
                    {editOrderMode ? (
                        <div className="border rounded-lg overflow-x-auto">
                            <table className="w-full text-sm min-w-[700px]">
                                <thead className="sticky top-0 z-10">
                                    <tr className="bg-blue-50 text-slate-500">
                                        <th className="text-left p-3">Produit</th>
                                        <th className="text-right p-3">P.U.</th>
                                        <th className="text-center p-3">Mode</th>
                                        <th className="text-center p-3">Qté commandée</th>
                                        <th className="text-center p-3">Reçu</th>
                                        <th className="text-center p-3">Qté facturée</th>
                                        <th className="text-right p-3">Total</th>
                                        <th className="text-center p-3 w-16"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {editOrderItems.map((item, index) => {
                                        const billingInfo = getBillingInfo(item);
                                        const sp = supplierProducts.find(p => p.id === item.supplier_product_id);
                                        const orderUnit = sp?.order_unit || item.order_unit || 'pièce';
                                        const billingUnit = sp?.billing_unit || 'pièce';
                                        const hasPackaging = sp?.packaging_qty && sp.packaging_qty > 1;
                                        const currentMode = item.order_mode || 'packaging';
                                        const productName = item.product_name || sp?.supplier_name_product || sp?.industrial_product_name || 'Produit inconnu';
                                        return (
                                            <tr key={index} className="border-t">
                                                <td className="p-3 font-medium">{productName}</td>
                                                <td className="p-3 text-right text-xs">{billingInfo.priceLabel}</td>
                                                <td className="p-3 text-center">
                                                    {hasPackaging ? (
                                                        <div className="inline-flex rounded overflow-hidden border">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleUpdateOrderItemMode(index, 'packaging')}
                                                                className={`px-2 py-1 text-[11px] ${currentMode === 'packaging' ? 'bg-blue-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                                                                title={`Commander en ${orderUnit}`}
                                                                data-testid={`edit-mode-packaging-${index}`}
                                                            >
                                                                Colisage
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleUpdateOrderItemMode(index, 'detail')}
                                                                className={`px-2 py-1 text-[11px] ${currentMode === 'detail' ? 'bg-blue-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                                                                title={`Commander en ${billingUnit}`}
                                                                data-testid={`edit-mode-detail-${index}`}
                                                            >
                                                                Unité
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-slate-400">—</span>
                                                    )}
                                                </td>
                                                <td className="p-3 text-center">
                                                    <div className="flex items-center justify-center gap-1">
                                                        {(() => {
                                                            const isVariableWeight = !!sp?.variable_weight && currentMode !== 'packaging';
                                                            return (
                                                                <Input
                                                                    type="number"
                                                                    inputMode={isVariableWeight ? 'decimal' : 'numeric'}
                                                                    min={isVariableWeight ? '0' : '0'}
                                                                    step={isVariableWeight ? '0.01' : '1'}
                                                                    value={item.quantity}
                                                                    onFocus={(e) => e.target.select()}
                                                                    onChange={(e) => handleUpdateOrderItemQuantity(index, e.target.value)}
                                                                    className="w-16 text-center"
                                                                />
                                                            );
                                                        })()}
                                                        <span className="text-xs text-slate-500">{currentMode === 'detail' ? billingUnit : orderUnit}</span>
                                                    </div>
                                                </td>
                                                <td className="p-3 text-center">
                                                    <Input
                                                        type="number"
                                                        inputMode="decimal"
                                                        min="0"
                                                        step="0.01"
                                                        value={item.quantity_received ?? ''}
                                                        onFocus={(e) => e.target.select()}
                                                        onChange={(e) => handleUpdateOrderItemReceived(index, e.target.value)}
                                                        className="w-16 text-center"
                                                        data-testid={`edit-received-input-${index}`}
                                                    />
                                                </td>
                                                <td className="p-3 text-center">
                                                    {billingInfo.qtyBilled ? (
                                                        <span className="inline-flex items-center px-2 py-1 rounded bg-emerald-100 text-emerald-800 font-semibold text-xs">
                                                            {billingInfo.qtyBilled}
                                                        </span>
                                                    ) : '-'}
                                                </td>
                                                <td className="p-3 text-right font-medium">{formatCurrency(calculateLineTotal(item))}</td>
                                                <td className="p-3 text-center">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleRemoveItemFromEditOrder(index)}
                                                        className="text-red-500 hover:text-red-700"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr className="border-t bg-blue-50 font-semibold">
                                        <td colSpan="6" className="p-3 text-right">Total</td>
                                        <td className="p-3 text-right">
                                            {formatCurrency(editOrderItems.reduce((sum, item) => sum + calculateLineTotal(item), 0))}
                                        </td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    ) : (
                        /* Products table - View Mode with detailed units */
                        <div className="border rounded-lg overflow-x-auto">
                            <table className="w-full text-sm min-w-[600px]">
                                <thead className="sticky top-0 z-10">
                                    <tr className="bg-slate-50 text-slate-500">
                                        <th className="text-left p-3">Produit</th>
                                        <th className="text-right p-3">P.U.</th>
                                        <th className="text-center p-3">Qté commandée</th>
                                        <th className="text-center p-3">Qté facturée</th>
                                        <th className="text-center p-3">Reçu</th>
                                        <th className="text-right p-3">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedOrder.items?.map((item, index) => {
                                        const productName = item.supplier_product_name || 
                                            supplierProducts.find(sp => sp.id === item.supplier_product_id)?.supplier_name_product ||
                                            industrialProducts.find(p => p.id === item.industrial_product_id)?.name || 
                                            'Produit';
                                        
                                        // Use original_price if price was adjusted during reception
                                        const originalPrice = item.original_price ?? item.unit_price;
                                        const currentPrice = item.unit_price;
                                        const priceChanged = item.price_adjusted === true;
                                        
                                        // Get supplier product for unit info
                                        const sp = supplierProducts.find(p => p.id === item.supplier_product_id);
                                        const orderUnit = sp?.order_unit || 'pièce';
                                        const billingUnit = sp?.billing_unit || 'pièce';
                                        const invoiceUnit = sp?.invoice_unit || billingUnit;
                                        const packagingQty = sp?.packaging_qty || 1;
                                        const hasUnknownPackaging = sp?.unknown_packaging || false;
                                        
                                        // Use order_mode from item (not sell_by_detail from product)
                                        // If order_mode is missing (legacy data), infer from stored total
                                        let orderMode = item.order_mode;
                                        if (!orderMode) {
                                            const detailTotal = item.quantity * item.unit_price;
                                            const packagingTotal = item.quantity * (sp?.packaging_qty || 1) * item.unit_price;
                                            // If stored total matches detail calc within 1 cent, it's detail
                                            if (item.total != null && Math.abs(item.total - detailTotal) < 0.02 && Math.abs(detailTotal - packagingTotal) > 0.02) {
                                                orderMode = 'detail';
                                            } else {
                                                orderMode = 'packaging';
                                            }
                                        }
                                        const isDetailMode = orderMode === 'detail';
                                        
                                        // Determine mode label
                                        const getModeLabel = () => {
                                            if (isDetailMode) {
                                                return { label: 'Détail', className: 'bg-amber-50 text-amber-700 border-amber-200' };
                                            } else if (hasUnknownPackaging || !sp?.packaging_qty) {
                                                return { label: 'Unité', className: 'bg-slate-50 text-slate-600 border-slate-200' };
                                            } else {
                                                return { label: 'Colisage', className: 'bg-blue-50 text-blue-700 border-blue-200' };
                                            }
                                        };
                                        const modeLabel = getModeLabel();
                                        
                                        // Quantity received is stored directly on the order item (in order units)
                                        const quantityReceivedOrderUnits = item.quantity_received || 0;
                                        
                                        // Calculate expected billed quantity based on order_mode
                                        let expectedBilledQty = item.quantity;
                                        if (!isDetailMode && packagingQty) {
                                            expectedBilledQty = item.quantity * packagingQty;
                                        }
                                        
                                        // Calculate actual billed quantity based on reception
                                        // quantityReceivedOrderUnits is in order units (cartons)
                                        let actualBilledQty = 0;
                                        if (isDetailMode) {
                                            // Detail mode: received qty is directly in billing units
                                            actualBilledQty = quantityReceivedOrderUnits;
                                        } else {
                                            // Packaging mode: received qty (in order units) × packaging
                                            actualBilledQty = quantityReceivedOrderUnits * packagingQty;
                                        }
                                        
                                        const isComplete = quantityReceivedOrderUnits >= item.quantity;
                                        const isPartial = quantityReceivedOrderUnits > 0 && quantityReceivedOrderUnits < item.quantity;
                                        const hasReception = quantityReceivedOrderUnits > 0;
                                        
                                        // Calculate line total based on actual received
                                        // unit_price is per invoice_unit; if invoice_unit == order_unit, billing in cartons
                                        const invoicePerOrderUnit = !!sp?.packaging_qty
                                            && (sp?.invoice_unit || '').toLowerCase() === (sp?.order_unit || '').toLowerCase();
                                        // Detail mode with mixed units (invoice ≠ billing) → price is per packaging_qty billing units
                                        const detailMixedUnits = isDetailMode && !!sp?.packaging_qty
                                            && (sp?.billing_unit || '').toLowerCase()
                                            && (sp?.invoice_unit || '').toLowerCase()
                                            && (sp?.billing_unit || '').toLowerCase() !== (sp?.invoice_unit || '').toLowerCase();
                                        const effectiveUnitPrice = detailMixedUnits ? (currentPrice / packagingQty) : currentPrice;
                                        const displayedPriceUnit = detailMixedUnits ? billingUnit : invoiceUnit;
                                        const qtyForTotal = hasReception
                                            ? (invoicePerOrderUnit || isDetailMode ? quantityReceivedOrderUnits : actualBilledQty)
                                            : (item.closed ? 0 : (invoicePerOrderUnit || isDetailMode ? item.quantity : expectedBilledQty));
                                        const lineTotal = qtyForTotal * effectiveUnitPrice;

                                        // Display unit for quantity ordered/received
                                        const qtyOrderedUnit = isDetailMode ? billingUnit : orderUnit;
                                        
                                        return (
                                            <tr key={index} className="border-t">
                                                <td className="p-3">
                                                    <div className="flex flex-col">
                                                        <span className="font-medium">{productName}</span>
                                                        <Badge 
                                                            variant="outline" 
                                                            className={`w-fit mt-1 text-xs ${modeLabel.className}`}
                                                        >
                                                            {modeLabel.label}
                                                        </Badge>
                                                    </div>
                                                </td>
                                                <td className="p-3 text-right">
                                                    <div className="flex flex-col items-end">
                                                        <span className={`text-xs ${priceChanged ? 'text-amber-600 font-medium' : ''}`}>
                                                            {formatCurrency(effectiveUnitPrice)}/{displayedPriceUnit}
                                                        </span>
                                                        {priceChanged && (
                                                            <span className="text-xs text-slate-400 line-through">
                                                                {formatCurrency(detailMixedUnits ? (originalPrice / packagingQty) : originalPrice)}/{displayedPriceUnit}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="p-3 text-center">
                                                    <span className="inline-flex items-center px-2 py-1 rounded bg-blue-100 text-blue-800 font-semibold text-xs">
                                                        {item.quantity} {qtyOrderedUnit}
                                                    </span>
                                                </td>
                                                <td className="p-3 text-center">
                                                    <div className="flex flex-col items-center gap-0.5">
                                                        {/* Show billed quantity */}
                                                        <span className={`inline-flex items-center px-2 py-1 rounded font-semibold text-xs ${
                                                            hasReception ? (
                                                                isComplete ? 'bg-emerald-100 text-emerald-800' : 
                                                                isPartial ? 'bg-amber-100 text-amber-800' : 
                                                                'bg-slate-100 text-slate-600'
                                                            ) : 'bg-slate-100 text-slate-600'
                                                        }`}>
                                                            {hasReception ? actualBilledQty : (item.closed ? 0 : expectedBilledQty)} {billingUnit}
                                                        </span>
                                                        {/* Show expected vs received if partial */}
                                                        {isPartial && (
                                                            <span className="text-xs text-slate-400">
                                                                / {expectedBilledQty} {billingUnit} prévu
                                                            </span>
                                                        )}
                                                        {!hasReception && (
                                                            <span className={`text-xs ${item.closed ? 'text-red-400 font-medium' : 'text-slate-400'}`}>
                                                                {item.closed ? 'Clôturée' : '(prévu)'}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="p-3 text-center">
                                                    <div className="flex flex-col items-center gap-0.5">
                                                        <div className="flex items-center justify-center gap-1">
                                                            <span className={`font-semibold ${isComplete ? 'text-emerald-600' : isPartial ? 'text-amber-600' : 'text-slate-400'}`}>
                                                                {quantityReceivedOrderUnits} {qtyOrderedUnit}
                                                            </span>
                                                            {isComplete && (
                                                                <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">✓</span>
                                                            )}
                                                            {isPartial && (
                                                                <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Partiel</span>
                                                            )}
                                                        </div>
                                                        {/* Show equivalent in billing units if packaging */}
                                                        {!isDetailMode && packagingQty > 1 && quantityReceivedOrderUnits > 0 && (
                                                            <span className="text-xs text-slate-500">
                                                                = {actualBilledQty} {billingUnit}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="p-3 text-right font-medium">{formatCurrency(lineTotal)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    {(() => {
                                        // Calculate totals
                                        let actualTotal = 0;
                                        let expectedTotal = 0;
                                        let hasAnyReception = false;
                                        
                                        selectedOrder.items?.forEach(item => {
                                            const sp = supplierProducts.find(p => p.id === item.supplier_product_id);
                                            const packagingQty = sp?.packaging_qty || 1;
                                            let orderMode = item.order_mode;
                                            if (!orderMode) {
                                                const detailTotal = item.quantity * item.unit_price;
                                                const packagingTotal = item.quantity * packagingQty * item.unit_price;
                                                if (item.total != null && Math.abs(item.total - detailTotal) < 0.02 && Math.abs(detailTotal - packagingTotal) > 0.02) {
                                                    orderMode = 'detail';
                                                } else {
                                                    orderMode = 'packaging';
                                                }
                                            }
                                            const isDetailMode = orderMode === 'detail';
                                            const invoicePerOrderUnit = !!sp?.packaging_qty
                                                && (sp?.invoice_unit || '').toLowerCase() === (sp?.order_unit || '').toLowerCase();
                                            // Detail mode with mixed units → unit_price is per packaging_qty billing_units
                                            const detailMixedUnits = isDetailMode && !!sp?.packaging_qty
                                                && (sp?.billing_unit || '').toLowerCase()
                                                && (sp?.invoice_unit || '').toLowerCase()
                                                && (sp?.billing_unit || '').toLowerCase() !== (sp?.invoice_unit || '').toLowerCase();
                                            const effectivePrice = detailMixedUnits ? (item.unit_price / packagingQty) : item.unit_price;

                                            const qtyReceivedOrderUnits = item.quantity_received || 0;
                                            if (qtyReceivedOrderUnits > 0) hasAnyReception = true;

                                            let expectedBilledQty = item.quantity;
                                            if (!isDetailMode) {
                                                expectedBilledQty = item.quantity * packagingQty;
                                            }
                                            const expectedQtyForTotal = isDetailMode || invoicePerOrderUnit ? item.quantity : expectedBilledQty;
                                            expectedTotal += expectedQtyForTotal * effectivePrice;

                                            let actualBilledQty = qtyReceivedOrderUnits;
                                            if (!isDetailMode) {
                                                actualBilledQty = qtyReceivedOrderUnits * packagingQty;
                                            }
                                            const actualQtyForTotal = isDetailMode || invoicePerOrderUnit ? qtyReceivedOrderUnits : actualBilledQty;
                                            actualTotal += actualQtyForTotal * effectivePrice;
                                        });
                                        
                                        const isDifferent = Math.abs(actualTotal - expectedTotal) > 0.01;
                                        
                                        return (
                                            <>
                                                {hasAnyReception && isDifferent && (
                                                    <tr className="border-t bg-slate-50 text-slate-500">
                                                        <td colSpan="5" className="p-2 text-right text-sm">Total commandé</td>
                                                        <td className="p-2 text-right text-sm line-through">{formatCurrency(expectedTotal)}</td>
                                                    </tr>
                                                )}
                                                <tr className={`border-t font-semibold ${hasAnyReception ? 'bg-emerald-50' : 'bg-slate-50'}`}>
                                                    <td colSpan="4" className="p-3 text-center align-middle">
                                                        {(() => {
                                                            const { userNote } = splitOrderNotes(selectedOrder.notes);
                                                            if (editOrderMode || !userNote) return null;
                                                            return (
                                                                <div
                                                                    className="text-red-600 text-base font-semibold italic leading-snug whitespace-pre-wrap"
                                                                    data-testid="order-note-banner"
                                                                >
                                                                    ⚠ {userNote}
                                                                </div>
                                                            );
                                                        })()}
                                                    </td>
                                                    <td className="p-3 text-right whitespace-nowrap">
                                                        {hasAnyReception ? 'Total facturé' : 'Total'}
                                                    </td>
                                                    <td className="p-3 text-right whitespace-nowrap">
                                                        {formatCurrency(hasAnyReception ? actualTotal : expectedTotal)}
                                                    </td>
                                                </tr>
                                            </>
                                        );
                                    })()}
                                </tfoot>
                            </table>
                        </div>
                    )}

                    {/* System-generated reception annotations - technical info shown below the frame.
                        These are auto-appended by the backend (tarifs ajustés, produits ajoutés,
                        quantités clôturées) and must NOT be mixed with the supplier-facing note. */}
                    {!editOrderMode && (() => {
                        const { systemNotes } = splitOrderNotes(selectedOrder?.notes);
                        if (systemNotes.length === 0) return null;
                        return (
                            <div
                                className="text-xs text-slate-500 italic space-y-0.5 pl-1"
                                data-testid="order-system-notes"
                            >
                                {systemNotes.map((line, idx) => (
                                    <div key={idx}>· {line}</div>
                                ))}
                            </div>
                        );
                    })()}

                    {/* Notes - Editable only in edit mode. In view mode, the note is shown
                        inside the table footer (left of the totals), in red, see <tfoot>. */}
                    {editOrderMode && (
                        <div className="space-y-2">
                            <Label>Notes</Label>
                            <Input
                                value={editOrderNotes}
                                onChange={(e) => setEditOrderNotes(e.target.value)}
                                placeholder="Notes optionnelles"
                            />
                        </div>
                    )}
                    
                    {/* Reception History - Only in view mode */}
                    {!editOrderMode && receptionHistory.length > 0 && (
                        <details className="border rounded-lg">
                            <summary className="p-3 cursor-pointer text-sm font-semibold text-slate-600 hover:bg-slate-50">
                                Historique des réceptions ({receptionHistory.length})
                            </summary>
                            <div className="border-t overflow-x-auto">
                                <table className="w-full text-sm min-w-[500px]">
                                    <thead>
                                        <tr className="bg-slate-100 text-slate-600">
                                            <th className="p-2 text-left">Date</th>
                                            <th className="p-2 text-left">Produit</th>
                                            <th className="p-2 text-left">N° Doc</th>
                                            <th className="p-2 text-right">Qté</th>
                                            <th className="p-2 text-right">Prix</th>
                                            <th className="p-2 text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {receptionHistory.map((reception) => (
                                            <tr key={reception.id} className="border-t hover:bg-slate-50">
                                                <td className="p-2">{formatDate(reception.date)}</td>
                                                <td className="p-2">{reception.product_name}</td>
                                                <td className="p-2 text-slate-600">
                                                    {reception.document_number || <span className="text-slate-400 italic">-</span>}
                                                </td>
                                                <td className="p-2 text-right">{reception.quantity}</td>
                                                <td className="p-2 text-right">{formatCurrency(reception.unit_price || 0)}</td>
                                                <td className="p-2 text-center">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleEditReception(reception)}
                                                        title="Modifier"
                                                    >
                                                        <Pencil className="w-3 h-3" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDeleteReception(reception)}
                                                        className="text-red-500 hover:text-red-700"
                                                        title="Supprimer"
                                                    >
                                                        <Trash2 className="w-3 h-3" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </details>
                    )}
                </div>
                
                {/* Fixed Footer - Action Buttons */}
                <div className="flex-shrink-0 pt-4 border-t space-y-2">
                    {editOrderMode ? (
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={handleCancelEditOrder} className="flex-1">
                                Annuler
                            </Button>
                            <Button 
                                onClick={handleSaveOrderChanges} 
                                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                                disabled={editOrderItems.length === 0}
                                data-testid="save-order-changes-btn"
                            >
                                <Save className="w-4 h-4 mr-2" />
                                Enregistrer les modifications
                            </Button>
                        </div>
                    ) : (
                        <>
                            <div className="flex gap-2">
                                <Button
                                    onClick={() => openAuthPdf(ordersAPI.getPdfUrl(selectedOrder.id))}
                                    className="flex-1"
                                    variant="outline"
                                    data-testid="download-pdf-btn"
                                >
                                    <FileText className="w-4 h-4 mr-2" />
                                    Aperçu / Télécharger PDF
                                </Button>
                                <PrintButton size="default" testId="print-order-pdf-btn"
                                    url={() => ordersAPI.getPdfUrl(selectedOrder.id)} />
                            </div>
                            {/* Hide the big "Envoyer par email" button once the order is
                                received (partially or fully) — mirrors the Consolidation
                                page behavior where the Send action disappears as soon as
                                the reception is recorded. A small "Renvoyer" link remains
                                available for orders already emailed (see block below). */}
                            {selectedOrder.email_status === 'failed' && (
                                <div className="text-xs bg-red-50 border border-red-200 text-red-700 rounded p-2" data-testid="email-failed-alert">
                                    <b>Échec d'envoi de l'email</b> — le fournisseur n'a rien reçu.
                                    {selectedOrder.email_error && <div className="mt-0.5 text-red-600 break-words">{selectedOrder.email_error}</div>}
                                </div>
                            )}
                            {selectedOrder.email_status === 'retrying' && (
                                <div className="text-xs bg-amber-50 border border-amber-200 text-amber-700 rounded p-2" data-testid="email-retrying-alert">
                                    <b>Premier envoi en échec — renvoi automatique en cours</b> (tentative {selectedOrder.email_attempts || 1}/3).
                                    {selectedOrder.email_error && <div className="mt-0.5 text-amber-600 break-words">{selectedOrder.email_error}</div>}
                                </div>
                            )}
                            {canSendEmail && !selectedOrder.email_sent_at
                                && selectedOrder.status !== 'delivered'
                                && selectedOrder.status !== 'partially_received' && (
                                <Button
                                    onClick={() => { handleSendEmail(selectedOrder); setDetailDialog(false); }}
                                    className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                                    data-testid="send-email-from-detail"
                                >
                                    <Send className="w-4 h-4 mr-2" />
                                    Envoyer par email au fournisseur (PDF)
                                </Button>
                            )}
                            {canSendEmail && selectedOrder.email_sent_at && (
                                <div className="flex items-center justify-between gap-2 pt-1">
                                    <div className="flex items-center gap-1.5 text-xs text-emerald-700">
                                        <Send className="w-3.5 h-3.5" />
                                        <span>Envoyée le {formatDate(selectedOrder.email_sent_at)}{selectedOrder.email_status === 'sent_after_retry' ? ` — réussie après ${selectedOrder.email_attempts || 2} tentatives automatiques` : ''}</span>
                                    </div>
                                    <Button
                                        onClick={() => setResendConfirm(true)}
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 text-[11px] text-slate-500 hover:text-blue-600 px-2"
                                        data-testid="resend-email-btn"
                                    >
                                        <Send className="w-3 h-3 mr-1" />
                                        Renvoyer
                                    </Button>
                                </div>
                            )}
                            {/* WhatsApp share (Option A): click-to-chat.
                                - BIG button when at least one supplier contact has the
                                  send_whatsapp flag — these are the suppliers explicitly
                                  ready for WhatsApp.
                                - SMALL discreet icon-only button otherwise — keeps the
                                  ad-hoc "type a number" workflow available without
                                  pushing it on screens where it's not opted-in. */}
                            {canSendWhatsApp && (() => {
                                const supplier = suppliers.find(s => s.id === selectedOrder.supplier_id);
                                const hasOptInContact = (supplier?.contacts || []).some(
                                    c => c.send_whatsapp && (c.phone || '').replace(/\D/g, '').length >= 8
                                );
                                if (hasOptInContact) {
                                    return (
                                        <Button
                                            onClick={() => setWhatsappDialog(true)}
                                            variant="outline"
                                            className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                                            data-testid="whatsapp-share-btn"
                                        >
                                            <MessageCircle className="w-4 h-4 mr-2" />
                                            {selectedOrder.whatsapp_sent_at
                                                ? 'Repartager sur WhatsApp'
                                                : 'Partager sur WhatsApp (PDF + lien)'}
                                        </Button>
                                    );
                                }
                                return (
                                    <button
                                        type="button"
                                        onClick={() => setWhatsappDialog(true)}
                                        className="self-end inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-emerald-600 transition-colors px-1 py-0.5"
                                        title="Partager sur WhatsApp (aucun contact préconfiguré — saisie manuelle d'un numéro)"
                                        data-testid="whatsapp-share-btn-small"
                                    >
                                        <MessageCircle className="w-3 h-3" />
                                        <span>WhatsApp</span>
                                    </button>
                                );
                            })()}
                            {canSendWhatsApp && selectedOrder.whatsapp_sent_at && (
                                <div className="flex items-center gap-1.5 text-[11px] text-emerald-600 px-1">
                                    <MessageCircle className="w-3 h-3" />
                                    <span>
                                        Dernier partage le {formatDate(selectedOrder.whatsapp_sent_at)}
                                        {selectedOrder.whatsapp_logs?.length > 1
                                            ? ` · ${selectedOrder.whatsapp_logs.length} envois`
                                            : ''}
                                    </span>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </>
        )}
    </DialogContent>
</Dialog>

{/* Resend email confirmation */}
<AlertDialog open={resendConfirm} onOpenChange={setResendConfirm}>
    <AlertDialogContent data-testid="resend-confirm-dialog">
        <AlertDialogHeader>
            <AlertDialogTitle>Renvoyer la commande au fournisseur ?</AlertDialogTitle>
            <AlertDialogDescription>
                Cette commande a déjà été envoyée le{' '}
                <strong>{selectedOrder?.email_sent_at ? formatDate(selectedOrder.email_sent_at) : '—'}</strong>.
                Voulez-vous quand même la renvoyer ?
            </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
                onClick={() => {
                    handleSendEmail(selectedOrder);
                    setResendConfirm(false);
                    setDetailDialog(false);
                }}
                className="bg-blue-500 hover:bg-blue-600"
                data-testid="resend-confirm-btn"
            >
                Renvoyer
            </AlertDialogAction>
        </AlertDialogFooter>
    </AlertDialogContent>
</AlertDialog>

{/* WhatsApp share dialog (Option A — click-to-chat). Opens wa.me with a
    pre-filled message + auto-downloads the PDF so the user can attach it
    manually in the conversation. Logs the share on the order document. */}
<WhatsAppShareDialog
    open={whatsappDialog}
    onOpenChange={setWhatsappDialog}
    order={selectedOrder}
    supplier={selectedOrder ? suppliers.find(s => s.id === selectedOrder.supplier_id) : null}
    onShared={() => {
        // Refresh the parent list (and the detail dialog) so the "Dernier
        // partage" status updates immediately without forcing the user to
        // reopen the order detail view.
        if (typeof props.loadData === 'function') props.loadData();
    }}
/>

{/* Edit Reception Dialog */}
<EditReceptionDialog
    open={editReceptionDialog}
    onOpenChange={setEditReceptionDialog}
    editingReception={editingReception}
    setEditingReception={setEditingReception}
    onSave={handleSaveReceptionEdit}
/>

{/* Reception Dialog */}
<Dialog open={receptionDialog} onOpenChange={setReceptionDialog}>
    <DialogContent className="sm:max-w-[95vw] sm:w-full sm:h-[95vh] overflow-hidden flex flex-col">
        <DialogTitle className="sr-only">Réception de marchandise</DialogTitle>
        <div className="flex-shrink-0 flex flex-wrap items-center gap-3 pb-2 border-b pr-12 sm:pr-14">
            <h2 className="text-lg font-semibold whitespace-nowrap">Réception de marchandise</h2>
            <span className="text-sm text-slate-500">
                {selectedOrder?.order_number} - {selectedOrder?.supplier_name}
            </span>
            {selectedOrder?.created_at && (
                <span className="text-xs text-slate-400 flex items-center gap-1" data-testid="reception-order-date">
                    <Calendar className="w-3.5 h-3.5" />
                    Commandée le {new Date(selectedOrder.created_at).toLocaleDateString('fr-FR')}
                </span>
            )}
            <div className="ml-auto">
                <OrderableModeToggle mode={receptionOrderableMode} onChange={setReceptionOrderableMode} />
            </div>
        </div>

        {/* Add extra product row — for products delivered but not in the order */}
        {selectedOrder && availableExtraReceptionProducts && availableExtraReceptionProducts.length > 0 && (
            <div className="flex-shrink-0 border-b py-2 bg-amber-50/30">
                <div className="flex flex-wrap sm:flex-nowrap items-center gap-2">
                    <span className="text-xs font-semibold text-amber-700 whitespace-nowrap mr-1">Ajouter un produit livré :</span>
                    <div className="flex-1 min-w-0 w-full sm:w-auto">
                        <Select
                            value={extraReceptionItem.supplier_product_id}
                            onValueChange={(value) => {
                                const sp = supplierProducts.find(p => p.id === value);
                                setExtraReceptionItem({
                                    ...extraReceptionItem,
                                    supplier_product_id: value,
                                    unit_price: sp ? (sp.unit_price?.toString() || '') : ''
                                });
                            }}
                        >
                            <SelectTrigger className="h-8 text-xs" data-testid="extra-reception-product-select">
                                <SelectValue placeholder="Sélectionner un produit du fournisseur" />
                            </SelectTrigger>
                            <CategorizedProductSelectContent
                                products={availableExtraReceptionProducts}
                                industrialProducts={industrialProducts}
                            />
                        </Select>
                    </div>
                    <Input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        placeholder="Qté"
                        className="w-full sm:w-20 h-8 text-xs"
                        value={extraReceptionItem.quantity}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => setExtraReceptionItem({ ...extraReceptionItem, quantity: e.target.value })}
                        data-testid="extra-reception-quantity-input"
                    />
                    <Input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        placeholder="Prix"
                        className="w-full sm:w-24 h-8 text-xs"
                        value={extraReceptionItem.unit_price}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => setExtraReceptionItem({ ...extraReceptionItem, unit_price: e.target.value })}
                        data-testid="extra-reception-price-input"
                    />
                    <Button
                        type="button"
                        onClick={handleAddExtraReceptionItem}
                        className="bg-amber-500 hover:bg-amber-600 text-white h-8 px-3 flex-shrink-0"
                        data-testid="add-extra-reception-btn"
                    >
                        <Plus className="w-4 h-4" />
                    </Button>
                    <HideAddedToggle
                        checked={hideAddedProducts}
                        onCheckedChange={setHideAddedProducts}
                        testid="hide-added-toggle-extra-reception"
                    />
                </div>
            </div>
        )}

        {/* Scrollable Products Table */}
        <div className="flex-1 overflow-auto py-4 min-h-0">
            <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-slate-50 z-10">
                        <tr className="text-slate-500 text-xs uppercase">
                            <th className="text-left px-3 py-1.5">Produit</th>
                            <th className="text-center px-3 py-1.5">Mode</th>
                            <th className="text-center px-3 py-1.5">Commandé</th>
                            <th className="text-center px-3 py-1.5">À facturer</th>
                            <th className="text-center px-3 py-1.5">Reçu</th>
                            <th className="text-center px-3 py-1.5">Prix ({receptionItems[0]?.invoice_unit || 'u.'})</th>
                            <th className="text-center px-3 py-1.5">À réceptionner</th>
                            <th className="text-right px-3 py-1.5">Total ligne</th>
                        </tr>
                    </thead>
                    <tbody>
                        {receptionItems.map((item, index) => {
                            const remaining = item.quantity_ordered - item.quantity_received;
                            // Extra lines (added during reception) are never considered "complete":
                            // they always need an editable quantity + total.
                            const isComplete = !item.is_extra && remaining === 0;
                            const priceChanged = item.unit_price !== item.original_unit_price;

                            // Determine mode label for this item
                            const hasUnknownPackaging = item.unknown_packaging || false;
                            const getItemModeLabel = () => {
                                if (item.is_detail_mode) {
                                    return { label: 'Détail', className: 'bg-amber-50 text-amber-700 border-amber-200' };
                                } else if (hasUnknownPackaging || !item.packaging_qty || item.packaging_qty <= 1) {
                                    return { label: 'Unité', className: 'bg-slate-50 text-slate-600 border-slate-200' };
                                } else {
                                    return { label: 'Colisage', className: 'bg-blue-50 text-blue-700 border-blue-200' };
                                }
                            };
                            const itemModeLabel = getItemModeLabel();

                            // Calculate billed quantities
                            const qtyReceivedBilled = item.is_detail_mode || item.packaging_qty <= 1
                                ? item.quantity_received
                                : item.quantity_received * item.packaging_qty;
                            const qtyToReceiveBilled = item.is_detail_mode || item.packaging_qty <= 1
                                ? item.quantity_to_receive
                                : item.quantity_to_receive * item.packaging_qty;

                            // Unit for ordering/receiving
                            const receiveUnit = item.is_detail_mode ? item.billing_unit : item.order_unit;

                            // Line total — same logic as footer "À facturer", including detail-mode mixed units (invoice_unit ≠ billing_unit)
                            const detailMixedUnits = item.is_detail_mode && item.packaging_qty
                                && (item.billing_unit || '').toLowerCase()
                                && (item.invoice_unit || '').toLowerCase()
                                && (item.billing_unit || '').toLowerCase() !== (item.invoice_unit || '').toLowerCase();
                            const effectivePrice = detailMixedUnits ? (item.unit_price / item.packaging_qty) : item.unit_price;
                            let lineTotal = 0;
                            if (item.variable_weight) {
                                lineTotal = (parseFloat(item.actual_weight) || 0) * effectivePrice;
                            } else if (item.receive_by_detail) {
                                lineTotal = (item.quantity_to_receive_detail || 0) * effectivePrice;
                            } else {
                                lineTotal = qtyToReceiveBilled * effectivePrice;
                            }
                            
                            return (
                                <tr key={index} className={`border-t ${item.replaced ? 'bg-slate-100 opacity-60' : item.is_extra ? 'bg-amber-50/40' : (index % 2 === 1 ? 'bg-slate-100' : 'bg-white')}`}>
                                    <td className="px-3 py-1.5 font-medium">
                                        {item.product_name}
                                        {item.is_extra && !item.replaces_supplier_product_id && (
                                            <Badge variant="outline" className="ml-2 text-[10px] bg-amber-50 text-amber-700 border-amber-200 px-1.5 py-0">
                                                Supplément
                                            </Badge>
                                        )}
                                        {item.is_extra && item.replaces_supplier_product_id && (
                                            <Badge variant="outline" className="ml-2 text-[10px] bg-violet-50 text-violet-700 border-violet-200 px-1.5 py-0" title={`Remplace : ${item.replaces_product_name || ''}`}>
                                                ↔ Remplacement
                                            </Badge>
                                        )}
                                        {item.replaced && (
                                            <Badge variant="outline" className="ml-2 text-[10px] bg-slate-200 text-slate-600 border-slate-300 px-1.5 py-0">
                                                Remplacée (rupture)
                                            </Badge>
                                        )}
                                    </td>
                                    <td className="px-3 py-1.5 text-center">
                                        <Badge 
                                            variant="outline" 
                                            className={`text-xs ${itemModeLabel.className}`}
                                        >
                                            {itemModeLabel.label}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-1.5 text-center">
                                        <span className="font-semibold text-slate-700">
                                            {item.quantity_ordered} {receiveUnit}
                                        </span>
                                    </td>
                                    <td className="px-3 py-1.5 text-center">
                                        <span className="inline-flex items-center px-2 py-1 rounded bg-emerald-100 text-emerald-800 font-semibold text-xs">
                                            {item.expected_billed_qty} {item.invoice_unit}
                                        </span>
                                    </td>
                                    <td className="px-3 py-1.5 text-center">
                                        <div className="flex flex-col items-center">
                                            <span className={`font-semibold ${isComplete ? 'text-emerald-600' : 'text-slate-700'}`}>
                                                {item.quantity_received} {receiveUnit}
                                            </span>
                                            {!item.is_detail_mode && item.packaging_qty > 1 && item.quantity_received > 0 && (
                                                <span className="text-xs text-slate-500">
                                                    = {qtyReceivedBilled} {item.invoice_unit}
                                                </span>
                                            )}
                                            {isComplete && (
                                                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full mt-1">Complet</span>
                                            )}
                                            {!isComplete && item.quantity_received > 0 && (
                                                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full mt-1">Partiel</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-3 py-1.5 text-center">
                                        <div className="relative inline-flex items-center justify-center w-24 mx-auto">
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={item.unit_price}
                                                onChange={(e) => {
                                                    const newItems = [...receptionItems];
                                                    newItems[index].unit_price = parseFloat(e.target.value) || 0;
                                                    setReceptionItems(newItems);
                                                }}
                                                className={`w-24 h-7 text-center text-xs ${priceChanged ? 'border-amber-400 bg-amber-50' : ''}`}
                                                data-testid={`price-input-${index}`}
                                            />
                                            <div className="absolute left-full ml-1.5 flex flex-col items-start leading-[1.05] text-[10px]">
                                                {priceChanged && (
                                                    <span className="text-amber-600 whitespace-nowrap">
                                                        {formatCurrency(item.original_unit_price)}
                                                    </span>
                                                )}
                                                <label
                                                    className={`flex items-center gap-1 cursor-pointer select-none whitespace-nowrap ${priceChanged ? 'text-orange-700' : 'text-slate-500'}`}
                                                    title="Cocher pour remplacer le prix de référence du produit dans la fiche fournisseur."
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={!!item.override_supplier_price}
                                                        onChange={(e) => {
                                                            const newItems = [...receptionItems];
                                                            newItems[index].override_supplier_price = e.target.checked;
                                                            setReceptionItems(newItems);
                                                        }}
                                                        className="accent-orange-500 h-2.5 w-2.5"
                                                        data-testid={`override-price-${index}`}
                                                    />
                                                    MAJ tarif
                                                </label>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-3 py-1.5 text-center">
                                        {item.replaced ? (
                                            <span className="text-violet-600 text-xs">↔ remplacée</span>
                                        ) : isComplete ? (
                                            <span className="text-slate-400">-</span>
                                        ) : item.variable_weight ? (
                                            // Variable weight product - show weight input
                                            <div className="flex flex-col items-center gap-1">
                                                <div className="text-xs text-slate-500 mb-1">
                                                    Poids théorique: ~{item.theoretical_weight?.toFixed(2)} {item.invoice_unit}
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={item.actual_weight ?? ''}
                                                        onChange={(e) => {
                                                            const newItems = [...receptionItems];
                                                            newItems[index].actual_weight = e.target.value;
                                                            newItems[index].quantity_to_receive = remaining;
                                                            setReceptionItems(newItems);
                                                        }}
                                                        className="w-24 text-center border-amber-300 bg-amber-50"
                                                        placeholder="Poids réel"
                                                        data-testid={`weight-input-${index}`}
                                                    />
                                                    <span className="text-xs text-amber-600 font-medium">{item.invoice_unit}</span>
                                                </div>
                                                <span className="text-xs text-amber-500 flex items-center gap-1">
                                                    <Scale className="w-3 h-3" /> Poids variable
                                                </span>
                                            </div>
                                        ) : item.sell_by_detail && !item.is_detail_mode && item.packaging_qty > 1 ? (
                                            // Product supports detail reception - inline compact toggle
                                            <div className="flex items-center justify-center gap-1.5">
                                                {/* Toggle: order_unit vs invoice_unit (compact pill) */}
                                                <div className="inline-flex bg-slate-100 rounded p-0.5 text-[10px] leading-none">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const newItems = [...receptionItems];
                                                            newItems[index].receive_by_detail = false;
                                                            setReceptionItems(newItems);
                                                        }}
                                                        className={`px-1.5 py-1 rounded font-semibold transition-colors ${!item.receive_by_detail ? 'bg-blue-500 text-white' : 'text-slate-600'}`}
                                                        data-testid={`toggle-order-unit-${index}`}
                                                    >
                                                        {item.order_unit}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const newItems = [...receptionItems];
                                                            newItems[index].receive_by_detail = true;
                                                            newItems[index].quantity_to_receive_detail = remaining * item.packaging_qty;
                                                            setReceptionItems(newItems);
                                                        }}
                                                        className={`px-1.5 py-1 rounded font-semibold transition-colors ${item.receive_by_detail ? 'bg-purple-500 text-white' : 'text-slate-600'}`}
                                                        data-testid={`toggle-detail-unit-${index}`}
                                                    >
                                                        {item.invoice_unit}
                                                    </button>
                                                </div>
                                                {/* Input based on mode */}
                                                {item.receive_by_detail ? (
                                                    <div className="flex items-center gap-1">
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            value={item.quantity_to_receive_detail || 0}
                                                            onChange={(e) => {
                                                                const newItems = [...receptionItems];
                                                                newItems[index].quantity_to_receive_detail = parseInt(e.target.value) || 0;
                                                                setReceptionItems(newItems);
                                                            }}
                                                            className="w-16 h-7 text-center text-xs border-purple-300 bg-purple-50"
                                                            data-testid={`qty-detail-input-${index}`}
                                                        />
                                                        <span className="text-[11px] text-purple-600 font-medium">{item.invoice_unit}</span>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-1">
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            value={item.quantity_to_receive}
                                                            onChange={(e) => {
                                                                const newItems = [...receptionItems];
                                                                newItems[index].quantity_to_receive = parseInt(e.target.value) || 0;
                                                                setReceptionItems(newItems);
                                                            }}
                                                            className="w-16 h-7 text-center text-xs"
                                                            data-testid={`qty-input-${index}`}
                                                        />
                                                        <span className="text-[11px] text-emerald-600 font-medium">{item.order_unit}</span>
                                                    </div>
                                                )}
                                                {/* Equivalence inline */}
                                                {!item.receive_by_detail && item.quantity_to_receive > 0 && (
                                                    <span className="text-[10px] text-slate-400 italic">
                                                        ={item.quantity_to_receive * item.packaging_qty}&nbsp;{item.invoice_unit}
                                                    </span>
                                                )}
                                            </div>
                                        ) : !item.is_detail_mode && item.packaging_qty > 1 ? (
                                            // Product sold by carton only - compact horizontal layout
                                            <div className="flex items-center justify-center gap-1.5">
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    value={item.quantity_to_receive}
                                                    onChange={(e) => {
                                                        const newItems = [...receptionItems];
                                                        newItems[index].quantity_to_receive = parseInt(e.target.value) || 0;
                                                        setReceptionItems(newItems);
                                                    }}
                                                    className="w-16 h-7 text-center text-xs"
                                                    data-testid={`qty-input-${index}`}
                                                />
                                                <span className="text-[11px] text-emerald-600 font-medium">{item.order_unit}</span>
                                                {item.quantity_to_receive > 0 && (
                                                    <span className="text-[10px] text-slate-400 italic">
                                                        ={item.quantity_to_receive * item.packaging_qty}&nbsp;{item.invoice_unit}
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            // Standard product (detail mode or no packaging) - compact horizontal input
                                            <div className="flex items-center justify-center gap-1.5">
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    value={item.quantity_to_receive}
                                                    onChange={(e) => {
                                                        const newItems = [...receptionItems];
                                                        newItems[index].quantity_to_receive = parseInt(e.target.value) || 0;
                                                        setReceptionItems(newItems);
                                                    }}
                                                    className="w-16 h-7 text-center text-xs"
                                                    data-testid={`qty-input-${index}`}
                                                />
                                                <span className="text-[11px] text-emerald-600 font-medium">{item.invoice_unit}</span>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-3 py-1.5 text-right font-semibold tabular-nums" data-testid={`line-total-${index}`}>
                                        {item.replaced ? (
                                            <span className="text-slate-400 text-xs italic">remplacée</span>
                                        ) : isComplete ? (
                                            <span className="text-slate-300">-</span>
                                        ) : lineTotal > 0 ? (
                                            <span className={priceChanged ? 'text-amber-700' : 'text-slate-800'}>
                                                {formatCurrency(lineTotal)}
                                            </span>
                                        ) : (
                                            <span className="text-slate-300">0,00 €</span>
                                        )}
                                        {!item.is_extra && !item.replaced && !isComplete && handleOpenReplacement && (
                                            <button
                                                type="button"
                                                onClick={() => handleOpenReplacement(index)}
                                                className="ml-2 text-[10px] text-violet-500 hover:text-violet-700 hover:bg-violet-50 rounded px-1.5 py-0.5 border border-violet-200"
                                                title="Marquer ce produit en rupture et le remplacer par un autre produit du fournisseur"
                                                data-testid={`replace-product-${index}`}
                                            >
                                                ↔ Rupture
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
        
        {/* Compact footer: 1 ligne KPIs+saisies, checkbox optionnelle, actions */}
        <div className="flex-shrink-0 border-t pt-2 space-y-2">
            <div className="flex flex-wrap items-center gap-3">
                {/* Compact KPI: À réceptionner */}
                <div className="px-2.5 py-1 bg-blue-50 rounded-md text-xs leading-tight">
                    <div className="text-[10px] uppercase tracking-wide text-blue-600 font-semibold">À réceptionner</div>
                    <div className="font-semibold text-blue-800 tabular-nums">
                        {receptionItems.reduce((sum, i) => {
                            if (i.receive_by_detail) return sum + (i.quantity_to_receive_detail || 0);
                            const billedQty = i.is_detail_mode || i.packaging_qty <= 1
                                ? i.quantity_to_receive
                                : i.quantity_to_receive * i.packaging_qty;
                            return sum + billedQty;
                        }, 0)} {receptionItems[0]?.invoice_unit || 'u.'}
                    </div>
                </div>
                {/* Compact KPI: À facturer */}
                <div className="px-2.5 py-1 bg-emerald-50 rounded-md text-xs leading-tight">
                    <div className="text-[10px] uppercase tracking-wide text-emerald-600 font-semibold">À facturer</div>
                    <div className="font-semibold text-emerald-800 tabular-nums">
                        {formatCurrency(receptionItems.reduce((sum, i) => {
                            const detailMixedUnits = i.is_detail_mode && i.packaging_qty
                                && (i.billing_unit || '').toLowerCase()
                                && (i.invoice_unit || '').toLowerCase()
                                && (i.billing_unit || '').toLowerCase() !== (i.invoice_unit || '').toLowerCase();
                            const effectivePrice = detailMixedUnits ? (i.unit_price / i.packaging_qty) : i.unit_price;
                            if (i.variable_weight) return sum + ((parseFloat(i.actual_weight) || 0) * effectivePrice);
                            if (i.receive_by_detail) return sum + ((i.quantity_to_receive_detail || 0) * effectivePrice);
                            const billedQty = i.is_detail_mode || i.packaging_qty <= 1
                                ? i.quantity_to_receive
                                : i.quantity_to_receive * i.packaging_qty;
                            return sum + (billedQty * effectivePrice);
                        }, 0))}
                    </div>
                </div>

                {/* Inline reception date */}
                <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-slate-500" />
                    <Label htmlFor="reception-date" className="sr-only">Date de réception</Label>
                    <Input
                        id="reception-date"
                        type="date"
                        value={receptionDate}
                        onChange={(e) => setReceptionDate(e.target.value)}
                        className="h-8 text-xs w-[145px]"
                        data-testid="reception-date-input"
                    />
                </div>

                {/* Inline invoice number */}
                <div className="flex items-center gap-1.5 flex-1 min-w-[200px]">
                    <FileText className="w-3.5 h-3.5 text-slate-500" />
                    <Label htmlFor="document-number" className="sr-only">N° de document</Label>
                    <Input
                        id="document-number"
                        type="text"
                        placeholder="N° Facture / BL"
                        value={documentNumber}
                        onChange={(e) => setDocumentNumber(e.target.value)}
                        className="h-8 text-xs"
                        data-testid="document-number-input"
                    />
                </div>

                {/* Inline close-remaining toggle (only when relevant) */}
                {hasUnreceivedQuantities() && (
                    <label
                        htmlFor="close-remaining"
                        className="flex items-center gap-1.5 cursor-pointer px-2 py-1 rounded border border-amber-200 bg-amber-50"
                        title="En cochant cette option, les quantités restantes seront considérées comme non livrées et la commande sera marquée comme « Livrée »."
                    >
                        <Checkbox
                            id="close-remaining"
                            checked={closeRemainingQuantities}
                            onCheckedChange={setCloseRemainingQuantities}
                            data-testid="close-remaining-checkbox"
                            className="h-3.5 w-3.5"
                        />
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                        <span className="text-[11px] font-medium text-amber-800 whitespace-nowrap">Clôturer les quantités non reçues</span>
                    </label>
                )}

                {/* Actions */}
                <div className="ml-auto flex items-center gap-2">
                    {receptionItems.some(i => i.unit_price !== i.original_unit_price) && (
                        <span className="text-[11px] text-amber-600 font-medium hidden md:inline">
                            Tarifs modifiés
                        </span>
                    )}
                    <Button variant="outline" size="sm" className="h-8" onClick={() => setReceptionDialog(false)}>
                        Annuler
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSaveReceptionDraft}
                        disabled={receptionSaving}
                        className="h-8 border-amber-300 text-amber-700 hover:bg-amber-50"
                        data-testid="save-reception-draft-btn"
                    >
                        Brouillon
                    </Button>
                    <Button size="sm" onClick={handleSaveReception} disabled={receptionSaving} className="h-8 bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="confirm-reception-btn">
                        <PackageCheck className="w-3.5 h-3.5 mr-1.5" />
                        {receptionSaving ? 'Enregistrement…' : 'Valider la réception'}
                    </Button>
                </div>
            </div>
        </div>
    </DialogContent>
</Dialog>

{/* Delete Confirmation */}
<AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
    <AlertDialogContent>
        <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
                Êtes-vous sûr de vouloir supprimer cette commande ? Cette action est irréversible.
            </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-red-500 hover:bg-red-600">
                Supprimer
            </AlertDialogAction>
        </AlertDialogFooter>
    </AlertDialogContent>
</AlertDialog>

{/* Replacement Dialog — pick a substitute product when supplier is out of stock */}
{replacementDialog && (
<Dialog open={!!replacementDialog?.open} onOpenChange={(open) => {
    if (!open) setReplacementDialog({ open: false, originalIndex: null });
}}>
    <DialogContent className="sm:max-w-md" data-testid="replacement-dialog">
        <DialogHeader>
            <DialogTitle>Remplacement pour rupture fournisseur</DialogTitle>
            <DialogDescription>
                {(() => {
                    const orig = receptionItems[replacementDialog?.originalIndex];
                    if (!orig) return null;
                    return (<>Produit en rupture : <span className="font-semibold text-slate-800">{orig.product_name}</span></>);
                })()}
            </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
            <div>
                <Label className="text-xs">Produit de remplacement (même fournisseur)</Label>
                {(() => {
                    const orig = receptionItems[replacementDialog?.originalIndex];
                    if (!orig) return null;
                    const orderedIds = new Set(receptionItems.map(i => i.supplier_product_id));
                    const candidates = (supplierProducts || []).filter(p => {
                        if (orderedIds.has(p.id)) return false;
                        return p.supplier_id === selectedOrder?.supplier_id;
                    }).sort((a, b) => (a.supplier_name_product || '').localeCompare(b.supplier_name_product || '', 'fr', { sensitivity: 'base' }));
                    return (
                        <Select
                            value={replacementForm?.supplier_product_id || ''}
                            onValueChange={(v) => {
                                const sp = candidates.find(c => c.id === v);
                                setReplacementForm({
                                    supplier_product_id: v,
                                    quantity: replacementForm?.quantity || orig.quantity_ordered || 1,
                                    unit_price: sp?.unit_price ?? '',
                                });
                            }}
                        >
                            <SelectTrigger className="mt-1 h-9" data-testid="replacement-product-select">
                                <SelectValue placeholder="Choisir un produit..." />
                            </SelectTrigger>
                            <SelectContent>
                                {candidates.length === 0 ? (
                                    <div className="px-3 py-2 text-xs text-slate-500">Aucun autre produit de ce fournisseur.</div>
                                ) : candidates.map(c => (
                                    <SelectItem key={c.id} value={c.id}>
                                        {c.supplier_name_product || c.industrial_product_name || c.id}
                                        {typeof c.unit_price === 'number' && c.unit_price > 0 && (
                                            <span className="text-slate-400 ml-2 text-[10px]">({formatCurrency(c.unit_price)})</span>
                                        )}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    );
                })()}
            </div>
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <Label className="text-xs">Quantité reçue</Label>
                    <Input
                        type="number"
                        step="any"
                        min="0"
                        value={replacementForm?.quantity ?? ''}
                        onChange={(e) => setReplacementForm({ ...replacementForm, quantity: e.target.value })}
                        className="mt-1 h-9"
                        data-testid="replacement-qty-input"
                    />
                </div>
                <div>
                    <Label className="text-xs">Prix unitaire (€)</Label>
                    <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={replacementForm?.unit_price ?? ''}
                        onChange={(e) => setReplacementForm({ ...replacementForm, unit_price: e.target.value })}
                        className="mt-1 h-9"
                        data-testid="replacement-price-input"
                    />
                </div>
            </div>
        </div>
        <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReplacementDialog({ open: false, originalIndex: null })}>
                Annuler
            </Button>
            <Button
                onClick={handleConfirmReplacement}
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="confirm-replacement-btn"
            >
                Confirmer le remplacement
            </Button>
        </DialogFooter>
    </DialogContent>
</Dialog>
)}


{/* Manual Reception Dialog */}
<Dialog open={manualReceptionDialog} onOpenChange={(open) => {
    setManualReceptionDialog(open);
    if (!open) setEditingManualReception(null);
}}>
    <DialogContent className="sm:max-w-[95vw] sm:w-full sm:h-[95vh] overflow-hidden flex flex-col">
        {/* Compact header: title + form fields on same block */}
        <div className="flex-shrink-0 pb-2 border-b space-y-2">
            <div className="flex items-center gap-3 pr-12 sm:pr-14">
                <h2 className="text-lg font-semibold whitespace-nowrap">{editingManualReception ? 'Modifier la réception' : 'Nouvelle réception'}</h2>
                <span className="text-sm text-slate-500">
                    {editingManualReception 
                        ? editingManualReception.reception_number
                        : 'Saisie en stock d\'un achat effectué'}
                </span>
                <div className="ml-auto">
                    <OrderableModeToggle mode={manualReceptionOrderableMode} onChange={setManualReceptionOrderableMode} />
                </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="space-y-2">
                <Label>Fournisseur *</Label>
                <Select
                    value={manualReceptionForm.supplier_id}
                    onValueChange={(value) => setManualReceptionForm(prev => ({ ...prev, supplier_id: value, items: [] }))}
                >
                    <SelectTrigger data-testid="manual-reception-supplier-select">
                        <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                        {[...(orderCreationSuppliers || suppliers)].sort((a, b) => a.name.localeCompare(b.name, 'fr')).map(s => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div className="space-y-2">
                <Label>Site de réception *</Label>
                <Select
                    value={manualReceptionForm.site_id}
                    onValueChange={(value) => setManualReceptionForm(prev => ({ ...prev, site_id: value }))}
                >
                    <SelectTrigger data-testid="manual-reception-site-select">
                        <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                        {getAllowedSites(sites).map(s => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div className="space-y-2">
                <Label className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-emerald-600" />Date de réception *</Label>
                <Input
                    type="date"
                    value={manualReceptionForm.reception_date}
                    onChange={(e) => setManualReceptionForm(prev => ({ ...prev, reception_date: e.target.value }))}
                    data-testid="manual-reception-date-input"
                    className="!bg-emerald-50/40 !border-2 !border-emerald-200 hover:!border-emerald-400 focus-visible:!border-emerald-500"
                />
            </div>
            <div className="space-y-2">
                <Label className="flex items-center gap-1"><FileText className="w-3.5 h-3.5 text-emerald-600" />N° de pièce</Label>
                <Input
                    type="text"
                    placeholder="FAC-..."
                    value={manualReceptionForm.document_number || ''}
                    onChange={(e) => setManualReceptionForm(prev => ({ ...prev, document_number: e.target.value }))}
                    data-testid="manual-reception-doc-input"
                    className="!bg-emerald-50/40 !border-2 !border-emerald-200 hover:!border-emerald-400 focus-visible:!border-emerald-500"
                />
            </div>
        </div>
        </div>

        {/* Fixed - Add product section */}
        {manualReceptionForm.supplier_id && (
            <div className="flex-shrink-0 border-b py-1.5">
                <div className="flex flex-wrap sm:flex-nowrap items-end gap-2">
                    <div className="flex-1 min-w-0 w-full sm:w-auto">
                        <Select
                            value={manualReceptionItem.supplier_product_id}
                            onValueChange={(value) => {
                                const sp = supplierProducts.find(p => p.id === value);
                                setManualReceptionItem({
                                    ...manualReceptionItem,
                                    supplier_product_id: value,
                                    unit_price: sp ? sp.unit_price.toString() : ''
                                });
                            }}
                        >
                            <SelectTrigger className="h-8 text-xs" data-testid="manual-reception-product-select">
                                <SelectValue placeholder="Produit" />
                            </SelectTrigger>
                            <CategorizedProductSelectContent
                                products={availableManualReceptionProducts}
                                industrialProducts={industrialProducts}
                            />
                        </Select>
                    </div>
                    <div className="flex items-end gap-2 w-full sm:w-auto">
                        {(() => {
                            const selectedSp = supplierProducts.find(p => p.id === manualReceptionItem.supplier_product_id);
                            const isVariableWeight = !!selectedSp?.variable_weight;
                            return (
                                <Input
                                    type="number"
                                    inputMode={isVariableWeight ? 'decimal' : 'numeric'}
                                    min={isVariableWeight ? '0.01' : '1'}
                                    step={isVariableWeight ? '0.01' : '1'}
                                    placeholder="Qté"
                                    className="flex-1 sm:w-20 h-8 text-xs"
                                    value={manualReceptionItem.quantity}
                                    onFocus={(e) => e.target.select()}
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        const parsed = isVariableWeight ? (parseFloat(raw) || 0) : (raw === '' ? 0 : Math.floor(parseFloat(raw) || 0));
                                        setManualReceptionItem({ ...manualReceptionItem, quantity: parsed });
                                    }}
                                    data-testid="manual-reception-quantity-input"
                                />
                            );
                        })()}
                        <Input
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            min="0"
                            placeholder="Prix"
                            className="flex-1 sm:w-24 h-8 text-xs"
                            value={manualReceptionItem.unit_price}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => setManualReceptionItem({ ...manualReceptionItem, unit_price: e.target.value })}
                            data-testid="manual-reception-price-input"
                        />
                        <Button
                            type="button"
                            onClick={handleAddManualReceptionItem}
                            className="bg-emerald-500 hover:bg-emerald-600 h-8 px-3 flex-shrink-0"
                            data-testid="add-manual-reception-item-btn"
                        >
                            <Plus className="w-4 h-4" />
                        </Button>
                        <HideAddedToggle
                            checked={hideAddedProducts}
                            onCheckedChange={setHideAddedProducts}
                            testid="hide-added-toggle-manual-reception"
                        />
                    </div>
                </div>
            </div>
        )}

        {/* Scrollable Products List */}
        <div className="flex-1 overflow-auto py-1 min-h-0">
            {manualReceptionForm.items.length > 0 && (
                <>
                {/* Desktop table */}
                <div className="hidden sm:block border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0 z-10">
                            <tr>
                                <th className="text-left px-2 py-1">Produit</th>
                                <th className="text-right px-2 py-1">Prix unit.</th>
                                <th className="text-right px-2 py-1">Qté</th>
                                <th className="text-right px-2 py-1">Total</th>
                                <th className="px-1 py-1"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {manualReceptionForm.items.map((item, index) => {
                                const itemSp = supplierProducts.find(p => p.id === item.supplier_product_id);
                                const itemIsVariableWeight = !!itemSp?.variable_weight;
                                return (
                                <tr key={index} className="border-t">
                                    <td className="px-2 py-1">
                                        {item.supplier_ref && <span className="font-mono text-xs text-slate-400 mr-1">{item.supplier_ref}</span>}
                                        <span>{item.product_name || item.supplier_product_name || item.industrial_product_name || 'Produit'}</span>
                                    </td>
                                    <td className="px-2 py-1 text-right">
                                        <div className="flex items-center gap-2 justify-end">
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                className="w-24 h-7 text-sm text-right"
                                                value={item.unit_price}
                                                onFocus={(e) => e.target.select()}
                                                onChange={(e) => {
                                                    const newPrice = parseFloat(e.target.value) || 0;
                                                    setManualReceptionForm(prev => ({
                                                        ...prev,
                                                        items: prev.items.map((it, i) => i === index ? { ...it, unit_price: newPrice } : it)
                                                    }));
                                                }}
                                            />
                                            <label
                                                className="flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer select-none whitespace-nowrap"
                                                title="Cocher pour remplacer le prix de référence du produit dans la fiche fournisseur."
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={!!item.override_supplier_price}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setManualReceptionForm(prev => ({
                                                            ...prev,
                                                            items: prev.items.map((it, i) => i === index ? { ...it, override_supplier_price: checked } : it)
                                                        }));
                                                    }}
                                                    className="accent-orange-500 h-2.5 w-2.5"
                                                    data-testid={`manual-rcp-override-${index}`}
                                                />
                                                MAJ tarif
                                            </label>
                                        </div>
                                    </td>
                                    <td className="px-2 py-1 text-right">
                                        <Input
                                            type="number"
                                            inputMode={itemIsVariableWeight ? 'decimal' : 'numeric'}
                                            step={itemIsVariableWeight ? '0.01' : '1'}
                                            min={itemIsVariableWeight ? '0.01' : '1'}
                                            className="w-24 h-7 text-sm text-right ml-auto"
                                            value={item.quantity}
                                            onFocus={(e) => e.target.select()}
                                            onChange={(e) => {
                                                const raw = e.target.value;
                                                const newQty = itemIsVariableWeight
                                                    ? (parseFloat(raw) || 0)
                                                    : (raw === '' ? 0 : Math.floor(parseFloat(raw) || 0));
                                                setManualReceptionForm(prev => ({
                                                    ...prev,
                                                    items: prev.items.map((it, i) => i === index ? { ...it, quantity: newQty } : it)
                                                }));
                                            }}
                                        />
                                    </td>
                                    <td className="px-2 py-1 text-right font-medium">{formatCurrency(item.quantity * item.unit_price)}</td>
                                    <td className="px-1 py-1 text-right">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                                            onClick={() => handleRemoveManualReceptionItem(index)}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                        <tfoot className="bg-emerald-50">
                            <tr>
                                <td colSpan="3" className="px-2 py-1 text-right font-semibold">Total :</td>
                                <td className="px-2 py-1 text-right font-bold text-emerald-700">{formatCurrency(calculateManualReceptionTotal())}</td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                {/* Mobile card view */}
                <div className="sm:hidden space-y-2">
                    {manualReceptionForm.items.map((item, index) => {
                        const itemSp = supplierProducts.find(p => p.id === item.supplier_product_id);
                        const itemIsVariableWeight = !!itemSp?.variable_weight;
                        return (
                        <div key={index} className="border rounded-lg p-2 bg-white">
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="flex-1 min-w-0">
                                    {item.supplier_ref && <div className="font-mono text-[10px] text-slate-400">{item.supplier_ref}</div>}
                                    <div className="font-medium text-sm break-words">{item.product_name || item.supplier_product_name || item.industrial_product_name || 'Produit'}</div>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-red-500 flex-shrink-0"
                                    onClick={() => handleRemoveManualReceptionItem(index)}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </Button>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-0.5">
                                    <Label className="text-[10px] text-slate-500">Prix unit.</Label>
                                    <Input
                                        type="number"
                                        inputMode="decimal"
                                        step="0.01"
                                        min="0"
                                        className="h-9 text-sm text-right"
                                        value={item.unit_price}
                                        onFocus={(e) => e.target.select()}
                                        onChange={(e) => {
                                            const newPrice = parseFloat(e.target.value) || 0;
                                            setManualReceptionForm(prev => ({
                                                ...prev,
                                                items: prev.items.map((it, i) => i === index ? { ...it, unit_price: newPrice } : it)
                                            }));
                                        }}
                                    />
                                    <label
                                        className="flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer select-none"
                                        title="Remplacer le prix de référence dans la fiche fournisseur."
                                    >
                                        <input
                                            type="checkbox"
                                            checked={!!item.override_supplier_price}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                setManualReceptionForm(prev => ({
                                                    ...prev,
                                                    items: prev.items.map((it, i) => i === index ? { ...it, override_supplier_price: checked } : it)
                                                }));
                                            }}
                                            className="accent-orange-500 h-3 w-3"
                                            data-testid={`manual-rcp-override-mobile-${index}`}
                                        />
                                        MAJ tarif
                                    </label>
                                </div>
                                <div className="space-y-0.5">
                                    <Label className="text-[10px] text-slate-500">Quantité</Label>
                                    <Input
                                        type="number"
                                        inputMode={itemIsVariableWeight ? 'decimal' : 'numeric'}
                                        step={itemIsVariableWeight ? '0.01' : '1'}
                                        min={itemIsVariableWeight ? '0.01' : '1'}
                                        className="h-9 text-sm text-right"
                                        value={item.quantity}
                                        onFocus={(e) => e.target.select()}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            const newQty = itemIsVariableWeight
                                                ? (parseFloat(raw) || 0)
                                                : (raw === '' ? 0 : Math.floor(parseFloat(raw) || 0));
                                            setManualReceptionForm(prev => ({
                                                ...prev,
                                                items: prev.items.map((it, i) => i === index ? { ...it, quantity: newQty } : it)
                                            }));
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="flex items-center justify-between mt-2 pt-2 border-t text-xs">
                                <span className="text-slate-500">Total</span>
                                <span className="font-semibold text-emerald-600">{formatCurrency(item.quantity * item.unit_price)}</span>
                            </div>
                        </div>
                        );
                    })}
                    <div className="flex items-center justify-between px-3 py-2 bg-emerald-50 rounded-lg mt-2">
                        <span className="font-semibold text-emerald-700 text-sm">Total</span>
                        <span className="text-lg font-bold text-emerald-600">{formatCurrency(calculateManualReceptionTotal())}</span>
                    </div>
                </div>
                </>
            )}
            {manualReceptionForm.items.length === 0 && manualReceptionForm.supplier_id && (
                <div className="text-center py-8 text-slate-400">
                    Aucun produit ajouté
                </div>
            )}
        </div>

        {/* Fixed Footer - Notes & Buttons */}
        <div className="flex-shrink-0 border-t pt-4 space-y-4">
            <div className="space-y-2">
                <Label>Notes</Label>
                <Input
                    value={manualReceptionForm.notes}
                    onChange={(e) => setManualReceptionForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Notes optionnelles"
                    data-testid="manual-reception-notes-input"
                />
            </div>
            <DialogFooter>
                <Button variant="outline" onClick={() => { setManualReceptionDialog(false); setEditingManualReception(null); }}>
                    Annuler
                </Button>
                <Button onClick={handleSaveManualReception} disabled={manualReceptionSaving} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="confirm-manual-reception-btn">
                    <PackageCheck className="w-4 h-4 mr-2" />
                    {manualReceptionSaving ? 'Enregistrement…' : (editingManualReception ? 'Enregistrer les modifications' : 'Créer la réception')}
                </Button>
            </DialogFooter>
        </div>
    </DialogContent>
</Dialog>

{/* Manual Reception Detail Dialog (read-only viewer + duplicate / edit) */}
<ManualReceptionDetailDialog
    open={manualReceptionDetailDialog}
    onOpenChange={setManualReceptionDetailDialog}
    manualReception={selectedManualReception}
    onDuplicate={handleDuplicateManualReception}
    onEdit={handleEditManualReception}
/>

{/* Delete Manual Reception Confirmation Dialog */}
<DeleteManualReceptionAlert
    open={deleteManualReceptionDialog}
    onOpenChange={setDeleteManualReceptionDialog}
    receptionNumber={manualReceptionToDelete?.reception_number}
    onConfirm={handleConfirmDeleteManualReception}
/>
        </>
    );
}
